/**
 * 管理后台服务（需 admin 权限）：
 * 渠道 CRUD、模型映射 CRUD、退款审核、用户管理、系统配置。
 *
 * @module services/admin
 */
import type {
  Channel,
  ChannelHealth,
  ModelMapping,
  RefundRequest,
  SystemConfig,
  User,
  UserRole,
} from '@/types';
import { API } from '@/config/api';
import { request } from './http';

/** 创建/更新渠道入参。 */
export interface ChannelInput {
  name: string;
  type: string;
  baseUrl: string;
  modelList: string[];
  priority: number;
  enabled: boolean;
  healthStatus?: ChannelHealth;
}

/** 创建/更新模型映射入参。 */
export interface ModelMappingInput {
  model: string;
  channelId: number;
  actualModel: string;
  multiplier: number;
  priority: number;
}

/** 管理后台用户补丁。 */
export interface AdminUserPatch {
  role?: UserRole;
  status?: string;
  name?: string;
  balance?: string;
}

/** 更新系统配置入参。 */
export interface ConfigPutInput {
  configValue: string;
}

// ---- 渠道管理 ----
export async function listChannels(): Promise<Channel[]> {
  return request<Channel[]>({ method: 'GET', url: API.admin.channels });
}

export async function createChannel(input: ChannelInput): Promise<Channel> {
  return request<Channel>({
    method: 'POST',
    url: API.admin.channels,
    data: input,
  });
}

export async function updateChannel(
  id: number,
  input: ChannelInput,
): Promise<Channel> {
  return request<Channel>({
    method: 'PATCH',
    url: API.admin.channelDetail(id),
    data: input,
  });
}

export async function deleteChannel(id: number): Promise<void> {
  await request<void>({ method: 'DELETE', url: API.admin.channelDetail(id) });
}

export async function triggerChannelHealth(id: number): Promise<void> {
  await request<void>({ method: 'POST', url: API.admin.channelHealth(id) });
}

// ---- 模型映射管理 ----
export async function listModelMappings(): Promise<ModelMapping[]> {
  return request<ModelMapping[]>({ method: 'GET', url: API.admin.modelMappings });
}

export async function createModelMapping(
  input: ModelMappingInput,
): Promise<ModelMapping> {
  return request<ModelMapping>({
    method: 'POST',
    url: API.admin.modelMappings,
    data: input,
  });
}

export async function updateModelMapping(
  id: number,
  input: ModelMappingInput,
): Promise<ModelMapping> {
  return request<ModelMapping>({
    method: 'PATCH',
    url: API.admin.modelMappingDetail(id),
    data: input,
  });
}

export async function deleteModelMapping(id: number): Promise<void> {
  await request<void>({
    method: 'DELETE',
    url: API.admin.modelMappingDetail(id),
  });
}

// ---- 退款审核 ----
export async function listPendingRefunds(): Promise<RefundRequest[]> {
  return request<RefundRequest[]>({ method: 'GET', url: API.admin.pendingRefunds });
}

export async function approveRefund(id: number): Promise<RefundRequest> {
  return request<RefundRequest>({
    method: 'POST',
    url: API.admin.refundApprove(id),
  });
}

export async function rejectRefund(
  id: number,
  note?: string,
): Promise<RefundRequest> {
  return request<RefundRequest>({
    method: 'POST',
    url: API.admin.refundReject(id),
    data: note ? { adminNote: note } : undefined,
  });
}

export async function completeRefund(id: number): Promise<RefundRequest> {
  return request<RefundRequest>({
    method: 'POST',
    url: API.admin.refundComplete(id),
  });
}

// ---- 用户管理 ----
export async function listUsers(): Promise<User[]> {
  return request<User[]>({ method: 'GET', url: API.admin.users });
}

export async function patchUser(
  id: number,
  input: AdminUserPatch,
): Promise<User> {
  return request<User>({
    method: 'PATCH',
    url: API.admin.userDetail(id),
    data: input,
  });
}

// ---- 系统配置 ----
export async function listConfigs(): Promise<SystemConfig[]> {
  return request<SystemConfig[]>({ method: 'GET', url: API.admin.configs });
}

export async function putConfig(
  key: string,
  input: ConfigPutInput,
): Promise<SystemConfig> {
  return request<SystemConfig>({
    method: 'PUT',
    url: API.admin.configDetail(key),
    data: input,
  });
}
